// src/api/client.js
// Axios instance for the taxpack API. Ported from the native client:
// - attaches the access token to every request
// - on a 401, refreshes once via /auth/refresh (deduped) and retries
// Web uses localStorage instead of SecureStore. On refresh failure it clears
// tokens and emits "auth:logout" so AuthContext can drop to the login screen.
// Backend envelope is { success, data }, so callers read res.data.data.

import axios from "axios";
import { API_URL } from "../config";

const ACCESS_KEY = "cb_access";
const REFRESH_KEY = "cb_refresh";

export const tokenStore = {
  get access() { return localStorage.getItem(ACCESS_KEY); },
  get refresh() { return localStorage.getItem(REFRESH_KEY); },
  set({ accessToken, refreshToken }) {
    if (accessToken) localStorage.setItem(ACCESS_KEY, accessToken);
    if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

const client = axios.create({
  baseURL: API_URL,
  timeout: 30000,
});

client.interceptors.request.use((config) => {
  const token = tokenStore.access;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// single in-flight refresh shared across concurrent 401s
let refreshPromise = null;

client.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;

    if (error.response?.status === 401 && original && !original._retry) {
      original._retry = true;
      try {
        const refreshToken = tokenStore.refresh;
        if (!refreshToken) throw error;

        if (!refreshPromise) {
          // raw axios so this call does not loop back through the interceptor
          refreshPromise = axios
            .post(`${API_URL}/auth/refresh`, { refreshToken })
            .then((r) => r.data.data.tokens);
        }
        const tokens = await refreshPromise;
        refreshPromise = null;

        tokenStore.set(tokens);
        original.headers.Authorization = `Bearer ${tokens.accessToken}`;
        return client(original);
      } catch (e) {
        refreshPromise = null;
        tokenStore.clear();
        window.dispatchEvent(new Event("auth:logout"));
        throw error;
      }
    }

    return Promise.reject(error);
  }
);

export default client;
